import { t } from 'i18next';
import { blue } from 'theme/colors';
import { Box, SvgIcon, Tooltip } from '@mui/material';
import { IconCircleCheck, IconInfoCircle, IconXboxX } from '@tabler/icons-react';
import { Button } from '@atoms';
import { useUser } from '@hooks';
import { PendingProspect } from '../../data/useGetPendingProspects';
import { SelectedPendingProspect } from '../../data/useResolvePendingProspect/hooks/useResolvePendingProspect';
import styles from '../../styles';

/**
 * Approve / reject buttons for pending prospect rows
 * @param prospect - Pending prospect row
 * @param callback - Function triggered on action click
 */
export default function ActionButtons({
  prospect,
  callback,
}: {
  prospect: PendingProspect;
  callback: (d: SelectedPendingProspect) => void;
}) {
  const { user } = useUser();
  const isOwnRequest = user?.email?.toLowerCase() === prospect?.createdBy?.toLowerCase();
  const isPending = prospect.status === 'PENDING';
  const showOwnRequestInfo = isPending && isOwnRequest;
  const showActions = isPending && !isOwnRequest;

  /**
   * Builds the payload for the confirmation modal
   * @param action - Approve or reject
   */
  const getSelectedProspect = (action: SelectedPendingProspect['action']) => ({
    id: prospect.id,
    companyName: prospect.customerName || '-',
    usersCount: prospect.usersCount ?? 0,
    action,
  });

  return (
    <Box display={'flex'} justifyContent={'space-around'}>
      {showOwnRequestInfo && (
        <Tooltip title={t('PENDING_PROSPECTS:OWN_REQUEST_INFO')} arrow>
          <Box display='flex' alignItems='center'>
            <SvgIcon fontSize='medium'>
              <IconInfoCircle color={blue.main} />
            </SvgIcon>
          </Box>
        </Tooltip>
      )}
      <ActionButton
        {...{
          tooltip: 'COMMON:APPROVE',
          icon: <IconCircleCheck color={blue.main} />,
          callback: () => callback(getSelectedProspect('APPROVE')),
          show: showActions,
        }}
      />
      <ActionButton
        {...{
          tooltip: 'COMMON:REJECT',
          icon: <IconXboxX color={blue.main} />,
          callback: () => callback(getSelectedProspect('REJECT')),
          show: showActions,
        }}
      />
    </Box>
  );
}

interface ActionButtonProps {
  tooltip: string;
  icon: React.ReactNode;
  callback: () => void;
  show: boolean;
}

/**
 * Button icon component of action buttons column
 * @param tooltip text to show on tooltip
 * @param icon icon to render inside button
 * @param callback function to trigger on button click
 * @param show if true renders the button, if false renders an empty fragment
 */
const ActionButton = ({ tooltip, icon, callback, show }: ActionButtonProps) => {
  if (!show) return <></>;
  return (
    <Tooltip title={t(tooltip)} arrow>
      <Box onClick={(event) => event.stopPropagation()}>
        <Button
          customStyle={{
            button: styles.actionButton,
            box: styles.actionButtonBox,
          }}
          onClick={(event) => {
            event.stopPropagation();
            callback();
          }}
          content={<SvgIcon fontSize='medium'>{icon}</SvgIcon>}
        />
      </Box>
    </Tooltip>
  );
};
